import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/pages/_billing-history.scss';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { formatDate } from '../utils/dateFormatter';

interface BillingRecord {
  _id: string;
  period_start: string;
  period_end: string;
  tokens_used: number;
  messages_count: number;
  billable_tokens: number;
  billable_messages: number;
  period_cost: number;
  currency: string;
  status: 'pending' | 'billed' | 'credited' | 'voided';
  source_context: string;
  createdAt: string;
  metadata?: {
    invoice_url?: string;
  };
}

interface BillingHistoryResponse {
  success: boolean;
  data: {
    usageRecords: BillingRecord[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  };
}

type FilterType = 'all' | 'subscription' | 'refund' | 'one-time';

// Placeholder data generator
const getPlaceholderData = (): BillingRecord[] => {
  return [
    {
      _id: 'placeholder-1',
      period_start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
      period_end: new Date().toISOString(),
      tokens_used: 15000,
      messages_count: 75,
      billable_tokens: 0,
      billable_messages: 25,
      period_cost: 150,
      currency: 'PHP',
      status: 'billed',
      source_context: 'chat',
      createdAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(), // 25 days ago
      metadata: {
        invoice_url: '#'
      }
    },
    {
      _id: 'placeholder-2',
      period_start: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(), // 60 days ago
      period_end: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      tokens_used: 25000,
      messages_count: 120,
      billable_tokens: 10000,
      billable_messages: 70,
      period_cost: 250,
      currency: 'PHP',
      status: 'billed',
      source_context: 'assessment',
      createdAt: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000).toISOString(), // 55 days ago
      metadata: {}
    },
    {
      _id: 'placeholder-3',
      period_start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days ago
      period_end: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      tokens_used: 5000,
      messages_count: 25,
      billable_tokens: 0,
      billable_messages: 0,
      period_cost: 0,
      currency: 'PHP',
      status: 'credited',
      source_context: 'chat',
      createdAt: new Date(Date.now() - 85 * 24 * 60 * 60 * 1000).toISOString(), // 85 days ago
      metadata: {}
    }
  ];
};

const BillingHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  // Fetch billing history data
  useEffect(() => {
    const fetchBillingHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        const response = await fetch(API_CONFIG.ENDPOINTS.BILLING_HISTORY, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          // If API fails, show placeholder data instead of error

          setRecords(getPlaceholderData());
          return;
        }

        const data: BillingHistoryResponse = await response.json();
        setRecords(data.data.usageRecords);
      } catch (err) {

        // Show placeholder data instead of error
        setRecords(getPlaceholderData());
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchBillingHistory();
    }
  }, [user]);

  // Filter and search records
  useEffect(() => {
    let filtered = records;

    // Apply status filter
    if (activeFilter !== 'all') {
      switch (activeFilter) {
        case 'subscription':
          filtered = filtered.filter(record =>
            record.status === 'billed' && record.source_context !== 'refund'
          );
          break;
        case 'refund':
          filtered = filtered.filter(record => record.status === 'credited');
          break;
        case 'one-time':
          filtered = filtered.filter(record =>
            record.source_context === 'assessment' || record.source_context === 'consultation'
          );
          break;
      }
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(record =>
        getDescription(record).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredRecords(filtered);
  }, [records, activeFilter, searchTerm]);

  const getDescription = (record: BillingRecord): string => {
    const context = record.source_context;
    let description = '';

    switch (context) {
      case 'chat':
        description = `${record.messages_count} messages, ${record.tokens_used} tokens`;
        break;
      case 'assessment':
        description = `Assessment completion - ${record.tokens_used} tokens`;
        break;
      case 'consultation':
        description = `Consultation - ${record.messages_count} messages`;
        break;
      case 'api':
        description = `API usage - ${record.tokens_used} tokens`;
        break;
      default:
        description = `Usage - ${record.messages_count} messages, ${record.tokens_used} tokens`;
    }

    // Add placeholder indicator if this is placeholder data
    if (record._id.startsWith('placeholder-')) {
      description += ' (placeholder)';
    }

    return description;
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: 'Pending', className: 'billing-history__status--pending' },
      billed: { label: 'Paid', className: 'billing-history__status--paid' },
      credited: { label: 'Refunded', className: 'billing-history__status--refunded' },
      voided: { label: 'Failed', className: 'billing-history__status--failed' }
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || statusMap.pending;

    return (
      <span className={`billing-history__status ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatCurrency = (amount: number, currency: string = 'PHP') => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleBack = () => {
    navigate('/panel/subscription');
  };

  if (loading) {
    return (
      <div className="billing-history-page">
        <div className="billing-history-page__content">
          <div className="loading-spinner" style={{ textAlign: 'center', padding: '2rem' }}>
            Loading billing history...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-history-page">
        <div className="billing-history-page__content">
          <div className="error-message" style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-history-page">
      <div className="billing-history-page__content">
        {/* Back Button */}
        <div className="billing-history-page__header">
          <button
            className="billing-history-page__back-button"
            onClick={handleBack}
            aria-label="Back to Subscription"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            <span>Back to Subscription</span>
          </button>
        </div>

        {/* Page Title */}
        <div className="billing-history-page__title-section">
          <h1 className="billing-history-page__title">Billing History</h1>
          {records.length > 0 && records[0]._id.startsWith('placeholder-') && (
            <p className="billing-history-page__placeholder-notice">
              Showing sample placeholder data - API not yet implemented
            </p>
          )}
        </div>

        {/* Search and Filters */}
        <div className="billing-history-page__controls">
          {/* Search Input */}
          <div className="billing-history-page__search">
            <div className="billing-history-page__search-input-wrapper">
              <span className="material-symbols-outlined billing-history-page__search-icon">search</span>
              <input
                type="text"
                className="billing-history-page__search-input"
                placeholder="Search by description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="billing-history-page__filters">
            {(['all', 'subscription', 'refund', 'one-time'] as FilterType[]).map((filter) => (
              <button
                key={filter}
                className={`billing-history-page__filter-tab ${
                  activeFilter === filter ? 'billing-history-page__filter-tab--active' : ''
                }`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Billing History Table */}
        <div className="billing-history-page__table-container">
          <table className="billing-history-page__table">
            <thead className="billing-history-page__table-header">
              <tr>
                <th className="billing-history-page__table-header-cell">Transaction Date</th>
                <th className="billing-history-page__table-header-cell">Description</th>
                <th className="billing-history-page__table-header-cell">Amount</th>
                <th className="billing-history-page__table-header-cell">Status</th>
                <th className="billing-history-page__table-header-cell">Invoice</th>
              </tr>
            </thead>
            <tbody className="billing-history-page__table-body">
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={5} className="billing-history-page__empty-state">
                    {records.length === 0 ? 'No billing history available' : 'No records match your search criteria'}
                    {records.length > 0 && records[0]._id.startsWith('placeholder-') && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>
                        Showing sample placeholder data
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filteredRecords.map((record) => (
                  <tr key={record._id} className="billing-history-page__table-row">
                    <td className="billing-history-page__table-cell">
                      {formatDate(record.createdAt)}
                    </td>
                    <td className="billing-history-page__table-cell billing-history-page__table-cell--description">
                      {getDescription(record)}
                    </td>
                    <td className="billing-history-page__table-cell">
                      {record.period_cost > 0 ? formatCurrency(record.period_cost, record.currency) : '-'}
                    </td>
                    <td className="billing-history-page__table-cell">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="billing-history-page__table-cell">
                      {record.metadata?.invoice_url ? (
                        <a
                          href={record.metadata.invoice_url}
                          className="billing-history-page__invoice-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                          <span className="material-symbols-outlined">download</span>
                        </a>
                      ) : (
                        <span className="billing-history-page__invoice-placeholder">
                          {record.status === 'voided' ? 'Details' : '-'}
                          <span className="material-symbols-outlined">info</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BillingHistoryPage;
